import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { FaCheckCircle, FaCrown, FaBoxOpen, FaAndroid } from 'react-icons/fa';
import PaymentMethodsBlock from '../components/PaymentMethodsBlock';
import { buildWhatsAppUrl } from '../constants/whatsapp';
import './Planos.css';

const FORMATTER = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// Compra com a Bientech = base; já possui o equipamento = +R$ 30
const OWN_EQUIPMENT_SURCHARGE = 30;

// BTOne — PulmoScan
// Plano Essencial desativado na landing (só Premium). Mude para true para reativar.
const SHOW_BTONE_ESSENTIAL = false;
const ESSENTIAL_BUNDLE_MONTHLY = 29.9;
const PREMIUM_BUNDLE_MONTHLY = 49.9;
const ESSENTIAL_MONTHLY = ESSENTIAL_BUNDLE_MONTHLY + OWN_EQUIPMENT_SURCHARGE;
const PREMIUM_MONTHLY = PREMIUM_BUNDLE_MONTHLY + OWN_EQUIPMENT_SURCHARGE;
const ESSENTIAL_ANNUAL = ESSENTIAL_MONTHLY * 10;
const PREMIUM_ANNUAL = PREMIUM_MONTHLY * 10;
const ESSENTIAL_BUNDLE_ANNUAL = ESSENTIAL_BUNDLE_MONTHLY * 10;
const PREMIUM_BUNDLE_ANNUAL = PREMIUM_BUNDLE_MONTHLY * 10;

// BTTwo — PulmoFlow
const BTTWO_PATIENT_BUNDLE_MONTHLY = 19.9;
const BTTWO_PRO_BUNDLE_MONTHLY = 59.9;
const BTTWO_OWN_SURCHARGE = OWN_EQUIPMENT_SURCHARGE;
const BTTWO_PATIENT_MONTHLY = BTTWO_PATIENT_BUNDLE_MONTHLY + BTTWO_OWN_SURCHARGE;
const BTTWO_PRO_MONTHLY = BTTWO_PRO_BUNDLE_MONTHLY + BTTWO_OWN_SURCHARGE;
const BTTWO_PATIENT_BUNDLE_ANNUAL = BTTWO_PATIENT_BUNDLE_MONTHLY * 10;
const BTTWO_PRO_BUNDLE_ANNUAL = BTTWO_PRO_BUNDLE_MONTHLY * 10;
const BTTWO_PATIENT_ANNUAL = BTTWO_PATIENT_MONTHLY * 10;
const BTTWO_PRO_ANNUAL = BTTWO_PRO_MONTHLY * 10;

const BTTWO_PATIENT_FEATURES = [
  'Plataforma web e aplicativo PulmoFlow',
  'Coletas com o BTTwo e vínculo com profissionais',
  'Profissional vinculado visualiza suas coletas',
  'Calendário com metas de coleta',
  'Registro e consulta de sintomas',
  'Medicamentos prescritos pelo profissional',
  'Alertas do profissional e do sistema',
  'Chat integrado com o profissional vinculado',
];

const BTTWO_PRO_FEATURES = [
  'Tudo do plano para paciente',
  'Coletas na clínica com pacientes vinculados (app)',
  'Prescrição e acompanhamento de medicamentos',
  'Emissão e visualização de alertas',
  'Metas de coleta para os pacientes',
  'Calendário de acompanhamento dos pacientes',
  'Análise aprofundada das coletas na plataforma web',
  'Predito e valor basal nas avaliações',
  'Análise comparativa ao longo do tempo',
  'Insights gerados com IA',
];

const BTONE_ESSENTIAL_FEATURES = [
  'Acesso somente ao aplicativo PulmoScan',
  'Até 30 pacientes',
  'Limite de 300 sessões',
  'Avaliação de PImax e PEmax',
  'Treinamento respiratório com jogos gamificados',
  'Relatórios das sessões com predito do paciente',
  'Gerenciamento de pacientes',
  'Gerenciamento de fisioterapeutas',
  'Geração de PDF das sessões realizadas',
];

const BTONE_PREMIUM_FEATURES = [
  'Acesso completo ao app e à plataforma web, sem limites',
  'Tudo do aplicativo PulmoScan, sem restrições',
  'Sessões com visão detalhada na plataforma web',
  'Análise comparativa das sessões ao longo do tempo',
  'Módulo de finanças para controle das sessões',
  'Relatórios completos',
  'Gerenciamento de múltiplas clínicas',
  'Gerenciamento de pacientes e fisioterapeutas',
];

const PlanCard = ({
  title,
  priceFormatted,
  periodSuffix,
  annualHint,
  description,
  features,
  featuresDense = false,
  highlight = false,
  bundleClass = '',
  ctaLabel,
  ctaHref,
  paymentBaseAmount,
  billingWord,
  paymentVariant,
  showFeatures = true,
}) => (
  <div className={`plan-card plan-card--compact ${highlight ? 'highlight' : ''} ${bundleClass}`.trim()}>
    {highlight && (
      <div className="plan-badge">
        <FaCrown className="plan-badge-icon" />
        <span>Mais popular</span>
      </div>
    )}
    <div className="plan-header">
      <h3 className="plan-title">{title}</h3>
      {description ? <p className="plan-description">{description}</p> : null}
    </div>

    <div className="plan-price">
      <div className="price-line">
        <span className="price-value">{priceFormatted}</span>
        <span className="price-period">{periodSuffix}</span>
        {annualHint ? <span className="price-hint">{annualHint}</span> : null}
      </div>
    </div>

    {showFeatures && features.length > 0 && (
      <ul className={`plan-features ${featuresDense ? 'plan-features--detailed' : ''}`.trim()}>
        {features.map((featureText, index) => (
          <li key={index}>
            <FaCheckCircle className="feature-icon" />
            <span>{featureText}</span>
          </li>
        ))}
      </ul>
    )}

    <PaymentMethodsBlock
      baseAmount={paymentBaseAmount}
      variant={paymentVariant}
      billingWord={billingWord}
    />

    <div className="plan-cta">
      <a className="cta-button" href={ctaHref}>
        {ctaLabel}
      </a>
    </div>
  </div>
);

const Planos = () => {
  const [billing, setBilling] = useState('monthly');
  const [ecosystem, setEcosystem] = useState('bttwo');

  const isAnnual = billing === 'annual';
  const periodSuffix = isAnnual ? 'por ano' : 'por mês';
  const annualHint = isAnnual ? 'Cobrança única anual — equivalente a 10 meses' : null;
  const billingWord = isAnnual ? 'pagamento anual único' : 'mensalidade';

  const fmt = (monthly, annual) => (isAnnual ? FORMATTER.format(annual) : FORMATTER.format(monthly));

  const wa = (message) => buildWhatsAppUrl(message);

  const bttwoMessages = {
    pacienteMonthly:
      'Olá! Tenho interesse na *Assinatura Paciente BTTwo (PulmoFlow)* — já possuo o equipamento, cobrança *mensal* (R$ 49,90/mês). Gostaria de mais informações. Obrigado!',
    pacienteAnnual:
      'Olá! Tenho interesse na *Assinatura Paciente BTTwo (PulmoFlow)* — já possuo o equipamento, cobrança *anual* (R$ 499,00/ano). Gostaria de mais informações. Obrigado!',
    profissionalMonthly:
      'Olá! Tenho interesse na *Assinatura Profissional BTTwo (PulmoFlow)* — já possuo o equipamento, cobrança *mensal* (R$ 89,90/mês). Gostaria de mais informações. Obrigado!',
    profissionalAnnual:
      'Olá! Tenho interesse na *Assinatura Profissional BTTwo (PulmoFlow)* — já possuo o equipamento, cobrança *anual* (R$ 899,00/ano). Gostaria de mais informações. Obrigado!',
    pacienteComEquipMonthly:
      'Olá! Tenho interesse na *Assinatura Paciente com compra do BTTwo pela Bientech*, cobrança *mensal* (R$ 19,90/mês). Quero combinar equipamento e ativação. Obrigado!',
    pacienteComEquipAnnual:
      'Olá! Tenho interesse na *Assinatura Paciente com compra do BTTwo pela Bientech*, cobrança *anual* (R$ 199,00/ano). Quero combinar equipamento e ativação. Obrigado!',
    profissionalComEquipMonthly:
      'Olá! Tenho interesse na *Assinatura Profissional com compra do BTTwo pela Bientech*, cobrança *mensal* (R$ 59,90/mês). Quero combinar equipamento e ativação. Obrigado!',
    profissionalComEquipAnnual:
      'Olá! Tenho interesse na *Assinatura Profissional com compra do BTTwo pela Bientech*, cobrança *anual* (R$ 599,00/ano). Quero combinar equipamento e ativação. Obrigado!',
  };

  const btoneMessages = {
    essencialMonthly:
      'Olá! Tenho interesse no *Plano Essencial* (já possuo o BTOne) com cobrança *mensal* (R$ 59,90/mês). Gostaria de mais informações para assinar. Obrigado!',
    essencialAnnual:
      'Olá! Tenho interesse no *Plano Essencial* (já possuo o BTOne) com cobrança *anual* (R$ 599,00/ano). Gostaria de mais informações para assinar. Obrigado!',
    premiumMonthly:
      'Olá! Tenho interesse no *Plano Premium* (já possuo o BTOne) com cobrança *mensal* (R$ 79,90/mês). Gostaria de mais informações para assinar. Obrigado!',
    premiumAnnual:
      'Olá! Tenho interesse no *Plano Premium* (já possuo o BTOne) com cobrança *anual* (R$ 799,00/ano). Gostaria de mais informações para assinar. Obrigado!',
    essencialComEquipMonthly:
      'Olá! Tenho interesse no *Plano Essencial com compra do BTOne pela Bientech*, cobrança *mensal* (R$ 29,90/mês). Quero combinar equipamento e ativação. Obrigado!',
    essencialComEquipAnnual:
      'Olá! Tenho interesse no *Plano Essencial com compra do BTOne pela Bientech*, cobrança *anual* (R$ 299,00/ano). Quero combinar equipamento e ativação. Obrigado!',
    premiumComEquipMonthly:
      'Olá! Tenho interesse no *Plano Premium com compra do BTOne pela Bientech*, cobrança *mensal* (R$ 49,90/mês). Quero combinar equipamento e ativação. Obrigado!',
    premiumComEquipAnnual:
      'Olá! Tenho interesse no *Plano Premium com compra do BTOne pela Bientech*, cobrança *anual* (R$ 499,00/ano). Quero combinar equipamento e ativação. Obrigado!',
  };

  const bttwoCardProps = {
    periodSuffix,
    annualHint,
    billingWord,
    paymentVariant: isAnnual ? 'plan-annual' : 'plan-monthly',
  };

  const btoneCardProps = {
    periodSuffix,
    annualHint,
    billingWord,
    paymentVariant: isAnnual ? 'plan-annual' : 'plan-monthly',
    featuresDense: true,
  };

  return (
    <div className="planos-page">
      <section className="planos-hero">
        <Container>
          <h1>Planos de assinatura</h1>
          <p className="planos-hero-lead">
            Escolha o ecossistema, o tipo de plano e a cobrança mensal ou anual.
          </p>

          <div className="planos-android-notice" role="note">
            <FaAndroid className="planos-android-notice__icon" aria-hidden />
            <span>
              Os aplicativos PulmoFlow e PulmoScan são <strong>somente para Android</strong>
            </span>
          </div>

          <div className="planos-hero-panel">
            <div className="planos-hero-panel__group">
              <span className="planos-hero-panel__label">Solução</span>
              <div className="ecosystem-toggle" role="tablist" aria-label="Ecossistema">
                <button
                  type="button"
                  role="tab"
                  aria-selected={ecosystem === 'bttwo'}
                  className={`ecosystem-toggle-btn ${ecosystem === 'bttwo' ? 'active' : ''}`}
                  onClick={() => setEcosystem('bttwo')}
                >
                  <span className="ecosystem-toggle-name">PulmoFlow</span>
                  <span className="ecosystem-toggle-sub">BTTwo</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={ecosystem === 'btone'}
                  className={`ecosystem-toggle-btn ${ecosystem === 'btone' ? 'active' : ''}`}
                  onClick={() => setEcosystem('btone')}
                >
                  <span className="ecosystem-toggle-name">PulmoScan</span>
                  <span className="ecosystem-toggle-sub">BTOne</span>
                </button>
              </div>
            </div>

            <span className="planos-hero-panel__divider" aria-hidden />

            <div className="planos-hero-panel__group">
              <span className="planos-hero-panel__label">Cobrança</span>
              <div className="billing-toggle" role="group" aria-label="Mensal ou anual">
                <button
                  type="button"
                  className={`billing-toggle-btn ${billing === 'monthly' ? 'active' : ''}`}
                  onClick={() => setBilling('monthly')}
                >
                  Mensal
                </button>
                <button
                  type="button"
                  className={`billing-toggle-btn ${billing === 'annual' ? 'active' : ''}`}
                  onClick={() => setBilling('annual')}
                >
                  Anual
                  <span className="billing-toggle-badge">Melhor valor</span>
                </button>
              </div>
            </div>

            <span className="planos-hero-panel__divider" aria-hidden />

            <Link to="/produtos" className="planos-hero-cta planos-hero-cta--secondary planos-hero-panel__equip">
              <FaBoxOpen aria-hidden />
              <span>Equipamentos</span>
            </Link>
          </div>
        </Container>
      </section>

      <Container className="planos-content">
        {ecosystem === 'bttwo' && (
          <>
            <section className="planos-pricing-section" aria-labelledby="planos-heading-bttwo-tem">
              <h2 id="planos-heading-bttwo-tem" className="planos-section-title">
                Já possuo o BTTwo
              </h2>
              <p className="planos-section-lead">Para quem já tem o medidor BTTwo.</p>
              <Row className="pricing-grid pricing-grid--two align-items-start">
                <Col xs={12} md={6} className="pricing-col">
                  <PlanCard
                    {...bttwoCardProps}
                    title="Plano para paciente"
                    priceFormatted={fmt(BTTWO_PATIENT_MONTHLY, BTTWO_PATIENT_ANNUAL)}
                    description="Para pacientes que usam o BTTwo e o PulmoFlow no dia a dia."
                    features={BTTWO_PATIENT_FEATURES}
                    featuresDense
                    ctaLabel="Assinar — plano paciente"
                    ctaHref={wa(isAnnual ? bttwoMessages.pacienteAnnual : bttwoMessages.pacienteMonthly)}
                    paymentBaseAmount={isAnnual ? BTTWO_PATIENT_ANNUAL : BTTWO_PATIENT_MONTHLY}
                  />
                </Col>
                <Col xs={12} md={6} className="pricing-col">
                  <PlanCard
                    {...bttwoCardProps}
                    title="Plano para o profissional"
                    priceFormatted={fmt(BTTWO_PRO_MONTHLY, BTTWO_PRO_ANNUAL)}
                    description="Para profissionais de saúde que acompanham e gerenciam pacientes com o BTTwo."
                    features={BTTWO_PRO_FEATURES}
                    featuresDense
                    ctaLabel="Assinar — plano profissional"
                    ctaHref={wa(isAnnual ? bttwoMessages.profissionalAnnual : bttwoMessages.profissionalMonthly)}
                    paymentBaseAmount={isAnnual ? BTTWO_PRO_ANNUAL : BTTWO_PRO_MONTHLY}
                  />
                </Col>
              </Row>
            </section>

            <section className="planos-pricing-section planos-pricing-section--sub" aria-labelledby="planos-heading-bttwo-com">
              <h2 id="planos-heading-bttwo-com" className="planos-section-title planos-section-title--sm">
                Compra do BTTwo com a Bientech
              </h2>
              <p className="planos-section-lead">Mesmos planos com valor menor na compra do equipamento conosco.</p>
              <Row className="pricing-grid pricing-grid--two align-items-start">
                <Col xs={12} md={6} className="pricing-col">
                  <PlanCard
                    {...bttwoCardProps}
                    title="Plano para paciente"
                    priceFormatted={fmt(BTTWO_PATIENT_BUNDLE_MONTHLY, BTTWO_PATIENT_BUNDLE_ANNUAL)}
                    description="Para pacientes que compram o BTTwo com a Bientech. Assinatura PulmoFlow com valor reduzido."
                    features={BTTWO_PATIENT_FEATURES}
                    featuresDense
                    bundleClass="plan-card--bundle"
                    ctaLabel="Contratar — plano paciente"
                    ctaHref={wa(isAnnual ? bttwoMessages.pacienteComEquipAnnual : bttwoMessages.pacienteComEquipMonthly)}
                    paymentBaseAmount={isAnnual ? BTTWO_PATIENT_BUNDLE_ANNUAL : BTTWO_PATIENT_BUNDLE_MONTHLY}
                  />
                </Col>
                <Col xs={12} md={6} className="pricing-col">
                  <PlanCard
                    {...bttwoCardProps}
                    title="Plano para o profissional"
                    priceFormatted={fmt(BTTWO_PRO_BUNDLE_MONTHLY, BTTWO_PRO_BUNDLE_ANNUAL)}
                    description="Para profissionais que compram o BTTwo com a Bientech. Assinatura PulmoFlow com valor reduzido."
                    features={BTTWO_PRO_FEATURES}
                    featuresDense
                    bundleClass="plan-card--bundle"
                    ctaLabel="Contratar — plano profissional"
                    ctaHref={wa(isAnnual ? bttwoMessages.profissionalComEquipAnnual : bttwoMessages.profissionalComEquipMonthly)}
                    paymentBaseAmount={isAnnual ? BTTWO_PRO_BUNDLE_ANNUAL : BTTWO_PRO_BUNDLE_MONTHLY}
                  />
                </Col>
              </Row>
            </section>
          </>
        )}

        {ecosystem === 'btone' && (
          <>
            <section className="planos-pricing-section" aria-labelledby="planos-heading-tem-equipamento">
              <h2 id="planos-heading-tem-equipamento" className="planos-section-title">
                Já possuo o BTOne
              </h2>
              <p className="planos-section-lead">
                {SHOW_BTONE_ESSENTIAL
                  ? 'Essencial e Premium para quem já tem o equipamento.'
                  : 'Plano Premium para quem já tem o equipamento.'}
              </p>
              <Row
                className={`pricing-grid ${SHOW_BTONE_ESSENTIAL ? 'pricing-grid--two' : 'pricing-grid--one'} align-items-start`}
              >
                {SHOW_BTONE_ESSENTIAL && (
                  <Col xs={12} md={6} className="pricing-col">
                    <PlanCard
                      {...btoneCardProps}
                      title="Essencial"
                      priceFormatted={fmt(ESSENTIAL_MONTHLY, ESSENTIAL_ANNUAL)}
                      description="Para profissionais que utilizam o PulmoScan pelo aplicativo com o BTOne (até 30 pacientes e 300 sessões)."
                      features={BTONE_ESSENTIAL_FEATURES}
                      ctaLabel="Assinar Essencial"
                      ctaHref={wa(isAnnual ? btoneMessages.essencialAnnual : btoneMessages.essencialMonthly)}
                      paymentBaseAmount={isAnnual ? ESSENTIAL_ANNUAL : ESSENTIAL_MONTHLY}
                    />
                  </Col>
                )}
                <Col xs={12} md={SHOW_BTONE_ESSENTIAL ? 6 : 8} className="pricing-col">
                  <PlanCard
                    {...btoneCardProps}
                    title="Premium"
                    priceFormatted={fmt(PREMIUM_MONTHLY, PREMIUM_ANNUAL)}
                    description="App e plataforma web completos, sem limites de uso."
                    features={BTONE_PREMIUM_FEATURES}
                    ctaLabel="Assinar Premium"
                    ctaHref={wa(isAnnual ? btoneMessages.premiumAnnual : btoneMessages.premiumMonthly)}
                    paymentBaseAmount={isAnnual ? PREMIUM_ANNUAL : PREMIUM_MONTHLY}
                  />
                </Col>
              </Row>
            </section>

            <section className="planos-pricing-section planos-pricing-section--sub" aria-labelledby="planos-heading-com-equipamento">
              <h2 id="planos-heading-com-equipamento" className="planos-section-title planos-section-title--sm">
                Compra do BTOne com a Bientech
              </h2>
              <p className="planos-section-lead">
                {SHOW_BTONE_ESSENTIAL
                  ? 'Mesmos planos com assinatura reduzida na compra do equipamento.'
                  : 'Plano Premium com assinatura reduzida na compra do equipamento.'}
              </p>
              <Row
                className={`pricing-grid ${SHOW_BTONE_ESSENTIAL ? 'pricing-grid--two' : 'pricing-grid--one'} align-items-start`}
              >
                {SHOW_BTONE_ESSENTIAL && (
                  <Col xs={12} md={6} className="pricing-col">
                    <PlanCard
                      {...btoneCardProps}
                      title="Essencial"
                      priceFormatted={fmt(ESSENTIAL_BUNDLE_MONTHLY, ESSENTIAL_BUNDLE_ANNUAL)}
                      description="Para quem compra o BTOne com a Bientech. Mesmos limites do Essencial, com assinatura reduzida."
                      features={BTONE_ESSENTIAL_FEATURES}
                      bundleClass="plan-card--bundle"
                      ctaLabel="Contratar Essencial"
                      ctaHref={wa(isAnnual ? btoneMessages.essencialComEquipAnnual : btoneMessages.essencialComEquipMonthly)}
                      paymentBaseAmount={isAnnual ? ESSENTIAL_BUNDLE_ANNUAL : ESSENTIAL_BUNDLE_MONTHLY}
                    />
                  </Col>
                )}
                <Col xs={12} md={SHOW_BTONE_ESSENTIAL ? 6 : 8} className="pricing-col">
                  <PlanCard
                    {...btoneCardProps}
                    title="Premium"
                    priceFormatted={fmt(PREMIUM_BUNDLE_MONTHLY, PREMIUM_BUNDLE_ANNUAL)}
                    description="Para quem compra o BTOne com a Bientech. App e plataforma web sem limites."
                    features={BTONE_PREMIUM_FEATURES}
                    bundleClass="plan-card--bundle"
                    ctaLabel="Contratar Premium"
                    ctaHref={wa(isAnnual ? btoneMessages.premiumComEquipAnnual : btoneMessages.premiumComEquipMonthly)}
                    paymentBaseAmount={isAnnual ? PREMIUM_BUNDLE_ANNUAL : PREMIUM_BUNDLE_MONTHLY}
                  />
                </Col>
              </Row>
            </section>
          </>
        )}
      </Container>
    </div>
  );
};

export default Planos;
